import React from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, useLocation } from 'react-router-dom';

import { useTimeModal } from '../../TimeModal/TimeModalContext';
import { formatNull, formatDate, isWithinLastHalfHour, formatDateTime } from '../../../Utils/GlobalUtils';

import FileViewerModal from '../../FileViewerModal';
import EditAccounting from '../../SectionsForCasePage/Components/EditAccounting';

import Loading from '../../Loading';
import LoadingLines from '../../LoadingLines';
import WarningPanel from '../../WarningPanel';

import ModalRight from '../../ModalRight';

import IconContainer from '../../IconContainer';
import { ReactComponent as CheckIcon } from '../../../icons/check.svg';
import { ReactComponent as XIcon } from '../../../icons/x-close.svg';
import { ReactComponent as TrashIcon } from '../../../icons/trash.svg';
import { ReactComponent as ReloadIcon } from '../../../icons/reload.svg';
import ButtonWithConfirmation from '../../ButtonWithConfirmation';

export default function UnconfirmedReports({ caseId }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { openTimeModal } = useTimeModal();

    const [reports, setReports] = React.useState([]);
    const [warning, setWarning] = React.useState('');
    const [reload, setReload] = React.useState(true);
    const [reloadBoxes, setReloadBoxes] = React.useState(1);
    const [loading, setLoading] = React.useState(true);

    const [isCase, setIsCase] = React.useState(false);

    const [isAdmin, setIsAdmin] = React.useState(false);
    
    React.useEffect(() => {
        const token = localStorage.getItem('token');
    
        try {
            const decodedToken = jwtDecode(token);
            if (decodedToken.role==='АппаратСРО') {
                setIsAdmin(true);
            } else {
                setIsAdmin(false);
            }
        } catch (error) {
            console.error('Ошибка при декодировании токена: ', error);
        }
    }, [])

    React.useEffect(() => {
        const token = localStorage.getItem('token');

        axios.get(`/api/get-unconfirmed-reports`, {
        headers: {
            Authorization: `Bearer ${token}`,
        }
        })
        .then((response) => {
            setWarning('');
            setReports(response.data);
        })
        .catch((error) => {
            setWarning(`${error.response?.data.message || 'Произошла неизвестная ошибка. Попробуйте повторить позднее'}`);
        })
        .finally(() => {
            setLoading(false);
            setReload(false);
        })
    }, [reload, caseId]);

    const [statusLoading, setStatusLoading] = React.useState('');
    const [showDecisionButtons, setShowDecisionButtons] = React.useState(null);

    const approveReport = (id, approveStatus) => {
        const token = localStorage.getItem('token');

        setStatusLoading(id);

        axios.put(`/api/change-report-status`, {
            id: id,
            approved: approveStatus,
        }, {
            headers: {
                Authorization: `Bearer ${token}`,
            }
        }).then(() => {
            openTimeModal('Статус изменен', 'success');
            setReload(true);
        }).catch((error) => {
            console.error(error);
            openTimeModal('Статус не изменен', 'error');
        }).finally(() => {
            setStatusLoading(null);
        })
    }


    const [confirmDeleteId, setConfirmDeleteId] = React.useState(null);
    const [deleteLoading, setDeleteLoading] = React.useState(null);
    
    React.useEffect(() => {
        if (confirmDeleteId !== null) {
        const timeout = setTimeout(() => setConfirmDeleteId(null), 3000);
        return () => clearTimeout(timeout);
        }
    }, [confirmDeleteId]);

    const handleDelete = (id) => {
        setDeleteLoading(id);

        axios.delete(`/api/delete-report`, {
            data: { id: id }
        })
        .then(() => {
            setReload(true);
            openTimeModal('Отчёт успешно удален', 'success');
        })
        .catch((error) => {
            openTimeModal('Ошибка при удалении отчёта', 'error');
        }).finally(() => {
            setDeleteLoading(null);
        })
    };

    const [isEditModal, setIsEditModal] = React.useState(false);
    const [currentId, setCurrentId] = React.useState('');
    const [currentAction, setCurrentAction] = React.useState('');
    
    const openSuperModal = (id, action) => {
        setCurrentId(id);
        setCurrentAction(action);
        setIsEditModal(true);
    };

    const [isFileViewerModal, setIsFileViewerModal] = React.useState(false);
    const [currentFileId, setCurrentFileId] = React.useState('');


    return (
        <>
        <ModalRight isOpen={isFileViewerModal} onClose={() => { window.location.hash = ''; setIsFileViewerModal(false) }} headline={'Поиск'}>
            <FileViewerModal closeModal={() => setIsFileViewerModal(false)} id={currentFileId} />
        </ModalRight>
        {isEditModal && <EditAccounting closeModal={() => setIsEditModal(false)} id={currentId} action={currentAction} caseId={caseId} onSuccess={() => {setReload(true); setReloadBoxes(reloadBoxes+1)}} /> }

        <div className='tableContainer'>        
            <div className='tableHeaderWrapper'>
                <div className='tableStatus'>
                    <h3>Непроверенные СРО отчёты</h3>
                    <a className='lastUpdateWrapper'>
                        {loading ? 
                            <Loading type='inline' /> 
                        : 
                            <IconContainer Icon={ReloadIcon} onClick={() => {setReload(true); setLoading(true)}} size = '14' color = 'black' className = '' />
                        }
                    </a>
                </div>
                <div className='tableHeader' style={{ gridTemplateColumns: '10fr 25fr 29fr 10fr 15fr 10fr 1fr' }}>
                    <div>Дата собрания (комитета)</div>
                    <div>Повестка (комментарий)</div>
                    <div>Отчёт</div>
                    <div>Дата загрузки</div>
                    <div>Статус</div>
                    <div></div>
                </div>
            </div>
            {(!reports && loading) ? <LoadingLines /> 
            : warning ? <WarningPanel text={warning} inTable={true} buttonText='Обновить' buttonAction={() => { setReload(true); setLoading(true) }} />
            : reports.map((report) => (
                <div 
                className='tableDoubleRow' 
                key={report.id} 
                >
                    <div 
                        className='row'
                        style={{ gridTemplateColumns: '10fr 25fr 29fr 10fr 15fr 10fr 1fr' }}
                    >
                        <div>{formatDate(report.date)}</div>
                        <div>{formatNull(report.comment)}</div>
                        <div>
                            {formatNull(report.report)}
                        </div>
                        <div>
                            <p>{formatDateTime(report.created_at)}</p>
                            {report.is_submitted_late ? <p style={{width: 'fit-content'}} className='colorCell red'>Сдан с нарушением срока</p>
                            : <p style={{width: 'fit-content'}} className='colorCell green'>Сдан вовремя</p>}
                        </div>
                        <div className={`${report.status==='Отклонено' ? 'redCell' : undefined}`}>
                            <p>{formatNull(report.status)} {report.approverDetails && `(${report.approverDetails?.lastname} ${report.approverDetails?.firstname} ${report.approverDetails?.surname} ${report.approverDetails?.email})`}</p>
                            <button type='button' className='tableButton blue' onClick={() => navigate(`/case/${report.case_id}`, { state: { from: location.pathname + location.search + location.hash }})}>Перейти к карточке</button>
                        </div>
                        <div className='horizontalCell'>
                        {isAdmin && (
                            statusLoading === report.id ? (
                                <Loading type="inline" />
                            ) : (
                                <>
                                    {!report.approver_id ? (
                                        <div className="actions">
                                            <IconContainer Icon={XIcon} label='Отклонить отчёт' onClick={() => approveReport(report.id, false)} size='16' color='red' />
                                            <IconContainer Icon={CheckIcon} label='Одобрить отчёт' onClick={() => approveReport(report.id, true)} size='16' color='green'/>
                                        </div>
                                    ) : (
                                        showDecisionButtons !== report.id ? (
                                            <button
                                                onClick={() => setShowDecisionButtons(report.id)}
                                                className="tableButton"
                                            >
                                                Изменить решение
                                            </button>
                                        ) : (
                                            <span className='horizontalCell'>
                                                <IconContainer Icon={XIcon} label='Отклонить отчёт' onClick={() => approveReport(report.id, false)} size='16' color='red' />
                                                <IconContainer Icon={CheckIcon} label='Одобрить отчёт' onClick={() => approveReport(report.id, true)} size='16' color='green'/>
                                            </span>
                                        )
                                    )}
                                </>
                            )
                        )}
                        </div>
                        <div style={{alignItems: 'end'}}>
                            {(isAdmin || isWithinLastHalfHour(report.created_at)) && 
                            <ButtonWithConfirmation
                                entryFunc={() => {handleDelete(report.id)}} 
                                Icon={TrashIcon} 
                                IconSize={17}
                                buttonName='Удалить' 
                                color='red' 
                            />}
                        </div>
                    </div>
                    <div 
                        className='horizontalRow'
                        style={{ gridTemplateColumns: '1fr' }}
                    >
                        {report.files.map((fileInfo) => (
                            <button type='button' className='tableButton' style={{}} onClick={() => {setCurrentFileId(fileInfo.id); setIsFileViewerModal(true)}}>
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" id="file"><path d="M20,8.94a1.31,1.31,0,0,0-.06-.27l0-.09a1.07,1.07,0,0,0-.19-.28h0l-6-6h0a1.07,1.07,0,0,0-.28-.19l-.09,0L13.06,2H7A3,3,0,0,0,4,5V19a3,3,0,0,0,3,3H17a3,3,0,0,0,3-3V9S20,9,20,8.94ZM14,5.41,16.59,8H14ZM18,19a1,1,0,0,1-1,1H7a1,1,0,0,1-1-1V5A1,1,0,0,1,7,4h5V9a1,1,0,0,0,1,1h5Z"></path></svg>
                                {fileInfo.name}
                            </button>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    </>
  )
}
